import dayjs from 'dayjs';
import {
  COD_BUCKET_LABELS,
  COD_DISCREPANCY_TREATMENT_LABELS,
  COD_FINANCE_ISSUE_REASON_LABELS,
  type CodFinanceQueue,
  COD_SETTLEMENT_SOURCE_LABELS,
  type CodBucket,
  codMethodLabel,
  type CodOrderListParams,
  type CodOrderRow,
  getCodOrders,
} from '@/services/api/codOrders';

/** One page big enough that the common case is a single request. */
const PAGE = 200;
/**
 * Ceiling on rows pulled for one export.
 *
 * Production runs ~3.2 COD orders a day, so 5,000 rows is roughly four years —
 * far past any range the filters can express. The cap exists so a mis-set
 * filter cannot turn a click into dozens of sequential requests, not because
 * the real data is ever near it.
 */
const MAX_ROWS = 5000;

/** RFC-4180 quoting: wrap in quotes and double any quote inside. */
const cell = (value: string | number | null | undefined): string => {
  if (value == null) return '';
  const text = String(value);
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

/**
 * Column layout: the order, then Leg 1, then Leg 2, then the end-to-end
 * result. Grouped that way because the question a reconciliation sheet is
 * opened to answer is "where did the money go wrong", and that is read left to
 * right through the two custody handoffs.
 *
 * Every discrepancy is signed and per leg, never merged: Leg 1 is measured
 * against the original COD amount, Leg 2 against what the rider actually
 * handed over. A single "Ada Selisih" column — which is what this exported
 * before — cannot say which of the two went wrong, and a PIC's short deposit
 * read as the rider's shortfall.
 */
const HEADERS = [
  'Booking ID',
  'Delivery ID',
  'Rider ID',
  // Was labelled 'Waybill Rider', which named the wrong thing entirely — the
  // column has always held the rider's name.
  'Nama Rider',
  'Kode Rider',
  'No. HP Rider',
  'Client',
  'Hub',
  'Owner (PIC)',
  'Status',
  'COD Amount',
  'Payment Method',
  // ---- Leg 1 · Rider → PIC
  'Leg 1 Settlement Source',
  'Leg 1 Selisih',
  // The column that turns a shortfall from prose into something summable —
  // "which rider owes how much" is answered by filtering this to Tagih ke Rider
  // and adding up the column beside it.
  'Leg 1 Tindak Lanjut',
  'Leg 1 Ada Bukti',
  // ---- Leg 2 · PIC → Finance
  'Leg 2 Deposit Method',
  'Leg 2 Disetor',
  'Leg 2 Selisih',
  'Leg 2 Tindak Lanjut',
  'Leg 2 Ada Bukti',
  // ---- Leg 3 · Finance
  // `Waktu Resolved` below says when the PIC CLAIMED to have banked it. These
  // say whether anybody found it on a statement. Before Leg 3 the two were the
  // same event and this file called both of them Selesai.
  'Ref Mutasi Bank',
  'Confirmed by Finance',
  'Waktu Confirmed by Finance',
  // ---- End to end
  'Hasil Akhir Selisih',
  'Hasil Akhir Status',
  'Waktu Resolved',
  // Kapan pengirimannya berhasil. Bersebelahan dengan 'Mulai Tahap' karena
  // keduanya mudah tertukar: yang ini titik tetap, yang itu jam yang di-reset
  // tiap tahap. Finance menutup buku dengan kolom ini.
  'Tanggal Selesai',
  'Mulai Tahap',
];

/**
 * Empty, not `0` / `Tidak`, when a leg has not closed.
 *
 * "This leg reconciled" and "this leg has not happened" are different facts,
 * and writing 0 for the second makes a spreadsheet sum look settled when half
 * the rows have not been confirmed yet.
 */
const legAmount = (value: number | null): string =>
  value == null ? '' : String(Math.round(value));

const legProof = (value: boolean | null): string =>
  value == null ? '' : value ? 'Ya' : 'Tidak';

/** Labelled, never the stored enum — this file is read by humans. */
const treatment = (value: string | null): string =>
  value ? (COD_DISCREPANCY_TREATMENT_LABELS[value] ?? value) : '';

const fmtTime = (value: string | null): string =>
  value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '';

const toRow = (row: CodOrderRow): string => {
  // End to end, deposited against the original COD — not the sum of the two
  // leg discrepancies. A rider Rp 10.000 short plus a PIC who covered it nets
  // to zero, and an order that reconciled at Leg 2 can still be short overall.
  const net =
    row.depositedAmount != null
      ? Math.round(row.depositedAmount) - Math.round(row.amount)
      : null;

  return [
    cell(row.bookingId),
    cell(row.deliveryUid),
    cell(row.riderId),
    cell(row.riderName),
    cell(row.riderCode),
    cell(row.riderPhone),
    cell(row.clientName),
    cell(row.hubName),
    cell(row.owner),
    cell(COD_BUCKET_LABELS[(row.status as CodBucket) ?? 'in_delivery']),
    // Raw digits, not `Rp 2.220.700` — a spreadsheet has to be able to sum
    // this column, and Indonesian thousand separators make it text.
    cell(Math.round(row.amount)),
    cell(row.paymentMethod),

    cell(
      row.settlementSource
        ? COD_SETTLEMENT_SOURCE_LABELS[row.settlementSource]
        : ''
    ),
    cell(legAmount(row.discrepancyLeg1)),
    cell(treatment(row.discrepancyTreatmentLeg1)),
    cell(legProof(row.leg1HasProof)),

    // Labelled, not the raw `bank_transfer` — Leg 1 above already exports its
    // label, and an operator-facing file should not carry stored enums.
    cell(row.depositMethod ? codMethodLabel(row.depositMethod) : ''),
    cell(legAmount(row.depositedAmount)),
    cell(legAmount(row.discrepancyLeg2)),
    cell(treatment(row.discrepancyTreatmentLeg2)),
    cell(legProof(row.leg2HasProof)),

    cell(row.financeBankRef),
    cell(row.financeConfirmedBy),
    cell(fmtTime(row.financeConfirmedAt)),

    cell(legAmount(net)),
    // A word, so the sheet can be filtered without writing a formula over the
    // signed column.
    cell(net == null ? '' : net === 0 ? 'Utuh' : net < 0 ? 'Kurang' : 'Lebih'),
    cell(fmtTime(row.resolvedAt)),
    cell(fmtTime(row.completedAt)),
    cell(fmtTime(row.stageStartedAt)),
  ].join(',');
};

/**
 * Export the CURRENT filter selection, not just the visible page.
 *
 * Exporting one page would be a trap: the operator filters to a bucket, sees
 * "47 rows", exports, and gets 20 — with nothing saying so. Pages are pulled
 * sequentially rather than in parallel because this is a back-office click on
 * a few hundred rows at most, and a burst of parallel requests against the
 * same filtered query buys nothing.
 */
export const downloadCodCsv = async (
  params: CodOrderListParams,
  expected: number
): Promise<void> => {
  const rows: CodOrderRow[] = [];
  let page = 1;

  for (;;) {
    const result = await getCodOrders({ ...params, page, limit: PAGE });
    rows.push(...result.rows);
    if (rows.length >= Math.min(result.total, MAX_ROWS)) break;
    if (result.rows.length === 0) break;
    page += 1;
  }

  downloadCsv(
    [HEADERS.join(','), ...rows.map(toRow)].join('\r\n'),
    `cod-reconciliation-${dayjs().format('YYYYMMDD-HHmm')}.csv`
  );

  if (rows.length < expected) {
    throw new Error(
      `Terunduh ${rows.length} dari ${expected} baris — batas export ${MAX_ROWS} baris. Sempitkan filternya.`
    );
  }
};

/**
 * Writes a CSV the browser downloads.
 *
 * The BOM is load-bearing: without it Excel on Windows reads the file as
 * latin-1 and every hub name with an accent arrives mangled, which is the first
 * thing anybody notices and the last thing they report.
 */
const downloadCsv = (csv: string, filename: string): void => {
  const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
};

/**
 * The Finance queue, led by the DEPOSIT rather than the order.
 *
 * Deliberately shaped differently from the board export above. That one is read
 * per order, left to right through the two custody handoffs, to answer "where
 * did this order's money go wrong". This one is read beside a bank statement:
 * every row repeats its deposit's identity and total so the file can be sorted
 * or filtered by statement line without losing which transfer a row belongs to.
 *
 * Repeating the batch columns on every row is the point, not redundancy — a
 * spreadsheet has no concept of a group header, and a merged cell is the first
 * thing to break when somebody sorts the sheet.
 */
const FINANCE_HEADERS = [
  // ---- the deposit (repeated per order, see above)
  'Setoran (PIC)',
  'Tanggal Setoran',
  'Metode Setoran',
  'Ref Mutasi Bank',
  'Nilai Setoran (batch)',
  'Jml Pesanan (batch)',
  'Client(s) batch',
  'Umur Setoran (hari)',
  // ---- the order
  'Delivery ID',
  'Booking ID',
  'Client',
  'Rider',
  'Hub',
  // Same column the screen shows, and what the completion filter reads.
  'Tanggal Selesai',
  'Disetor',
  'Selisih',
  'Tindak Lanjut Selisih',
  'Status Cocok',
  'Ditandai Cocok Oleh',
  'Waktu Tandai',
  'Status Bermasalah',
  'Alasan Bermasalah',
  'Catatan Bermasalah',
];

export const downloadCodFinanceCsv = (queue: CodFinanceQueue): void => {
  const rows = queue.batches.flatMap((batch) =>
    batch.orders.map((order) =>
      [
        cell(batch.depositedBy),
        cell(batch.depositedOn),
        cell(batch.method),
        cell(batch.financeBankRef),
        // Raw digits so the column sums; the batch total repeats per row and is
        // meant to be read, not added down the column.
        cell(Math.round(batch.totalAmount)),
        cell(batch.totalOrders),
        cell(batch.clientNames.join(' / ')),
        cell(order.ageDays),

        cell(order.deliveryUid),
        cell(order.bookingId),
        cell(order.clientName),
        cell(order.riderName),
        cell(order.hubName),
        cell(fmtTime(order.completedAt)),
        cell(Math.round(order.depositedAmount)),
        cell(
          order.discrepancyAmount === 0
            ? ''
            : Math.round(order.discrepancyAmount)
        ),
        cell(treatment(order.discrepancyTreatment)),
        // Three separate columns rather than one status, because Finance
        // filters on them independently: "what have I matched but not
        // confirmed" and "what is flagged" are different passes through the
        // same sheet.
        cell(order.financeBankRef ? 'Cocok' : 'Belum'),
        cell(order.financeMatchedBy),
        cell(fmtTime(order.financeMatchedAt)),
        cell(order.financeIssueReason ? 'Bermasalah' : ''),
        cell(
          order.financeIssueReason
            ? (COD_FINANCE_ISSUE_REASON_LABELS[order.financeIssueReason] ??
                order.financeIssueReason)
            : ''
        ),
        cell(order.financeIssueNote),
      ].join(',')
    )
  );

  downloadCsv(
    [FINANCE_HEADERS.join(','), ...rows].join('\r\n'),
    `cod-finance-${dayjs().format('YYYYMMDD-HHmm')}.csv`
  );
};
